import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, InputAdornment, InputBase, MenuItem, Paper, Select, Typography, alpha, useMediaQuery, useTheme,
} from '@mui/material';
import { LockOutlined, Search } from '@mui/icons-material';
import EmptyState from './EmptyState';

export interface SettingsShellItem {
  id: string;
  /** The group heading it sits under in the navigation. */
  group: string;
  title: string;
  description: string;
  icon?: React.ReactElement;
  render: () => React.ReactNode;
}

interface SettingsShellProps {
  title: string;
  subtitle: string;
  items: SettingsShellItem[];
  /** Shown when the user may open none of the items. */
  emptyDescription: string;
}

const NAV_WIDTH = 248;

/**
 * The one layout for a page of settings: grouped navigation with a filter
 * on the left, the chosen item on the right, `?item=` deep links, and a
 * picker in place of the navigation on narrow screens.
 */
const SettingsShell: React.FC<SettingsShellProps> = ({ title, subtitle, items, emptyDescription }) => {
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up('md'));
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState('');

  const current = items.find(i => i.id === params.get('item')) ?? items[0];
  const select = (id: string) => setParams({ item: id }, { replace: true });

  const groups = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const shown = q
      ? items.filter(i => `${i.title} ${i.description} ${i.group}`.toLowerCase().includes(q))
      : items;
    return shown.reduce<Array<{ name: string; items: SettingsShellItem[] }>>((acc, item) => {
      const last = acc[acc.length - 1];
      if (last && last.name === item.group) last.items.push(item);
      else acc.push({ name: item.group, items: [item] });
      return acc;
    }, []);
  }, [items, filter]);

  const header = (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h4" fontWeight={700}>{title}</Typography>
      <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
    </Box>
  );

  if (!current) {
    return (
      <Box>
        {header}
        <EmptyState icon={<LockOutlined />} title="Nothing to set up here" description={emptyDescription} />
      </Box>
    );
  }

  const tint = alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.08);

  const nav = (
    <Box
      data-tour="settings-rail"
      sx={{
        borderRight: 1,
        borderColor: 'divider',
        bgcolor: alpha(theme.palette.text.primary, 0.015),
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      {items.length > 6 && (
        <Box sx={{ p: 1.5, pb: 0.5 }}>
          <InputBase
            value={filter}
            onChange={e => setFilter(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && groups[0]) select(groups[0].items[0].id); }}
            placeholder="Find a setting"
            inputProps={{ 'aria-label': 'Find a setting' }}
            startAdornment={(
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            )}
            sx={{
              width: '100%',
              height: 36,
              px: 1.25,
              fontSize: '0.8125rem',
              borderRadius: '8px',
              border: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              transition: theme.transitions.create('border-color', { duration: 150 }),
              '&.Mui-focused': { borderColor: 'primary.main' },
            }}
          />
        </Box>
      )}
      <Box component="nav" sx={{ py: 1, overflowY: 'auto' }}>
        {groups.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, py: 2 }}>
            No setting matches "{filter.trim()}"
          </Typography>
        )}
        {groups.map((g, gi) => (
          <Box key={g.name} sx={{ mt: gi === 0 ? 0.5 : 2 }}>
            <Typography
              sx={{
                px: 2.5, pb: 0.5,
                fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                color: 'text.disabled',
              }}
            >
              {g.name}
            </Typography>
            {g.items.map(item => {
              const active = item.id === current.id;
              return (
                <Box
                  key={item.id}
                  component="button"
                  type="button"
                  onClick={() => select(item.id)}
                  aria-current={active ? 'page' : undefined}
                  sx={{
                    all: 'unset',
                    boxSizing: 'border-box',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    width: 'calc(100% - 16px)',
                    mx: 1,
                    mb: '2px',
                    px: 1.25,
                    minHeight: 34,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.8125rem',
                    fontWeight: active ? 600 : 500,
                    color: active ? 'primary.main' : 'text.secondary',
                    bgcolor: active ? tint : 'transparent',
                    transition: theme.transitions.create(['background-color', 'color'], { duration: 150 }),
                    '&:hover': { bgcolor: active ? tint : 'action.hover', color: active ? 'primary.main' : 'text.primary' },
                    '&:focus-visible': { outline: `2px solid ${alpha(theme.palette.primary.main, 0.5)}`, outlineOffset: -2 },
                    '& svg': { fontSize: 18, opacity: active ? 1 : 0.75 },
                  }}
                >
                  {item.icon}
                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.title}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );

  const picker = (
    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
      <Select
        fullWidth
        size="small"
        value={current.id}
        onChange={e => select(e.target.value)}
        renderValue={() => current.title}
      >
        {items.map((item, i) => [
          item.group !== items[i - 1]?.group && (
            <Typography key={`g-${item.group}`} sx={{ px: 2, pt: 1.5, pb: 0.5, fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.disabled' }}>
              {item.group}
            </Typography>
          ),
          <MenuItem key={item.id} value={item.id}>{item.title}</MenuItem>,
        ])}
      </Select>
    </Box>
  );

  return (
    <Box>
      {header}
      <Paper
        variant="outlined"
        sx={{
          borderRadius: '14px',
          overflow: 'hidden',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: `${NAV_WIDTH}px minmax(0, 1fr)` },
          alignItems: 'stretch',
        }}
      >
        {wide ? nav : picker}
        <Box data-tour="settings-content" sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>{current.title}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{current.description}</Typography>
          </Box>
          {current.render()}
        </Box>
      </Paper>
    </Box>
  );
};

export default SettingsShell;
